import * as ko from "knockout";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as Util from "../common/util";
import * as AuthenticationData from "../data/authenticationData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";

// this is the page a user would see after attempting to login and their password
// is expired and needs to be changed.  Or if the user has forgotten their
// password and needs to reset it.
// uRL: /ResetPassword/ACTION/TOKEN
//  aCTION : Expired or Reset
//  tOKEN : Unique ID supplies by the server to identify the user
// or /ResetPassword/TOKEN
// then its treated as if ACTION is Reset
export class ResetPasswordController implements IController {
    // references to DOM elements
    private container: JQuery;
    private viewModel: any = {};
    private token: string;

    // constructors
    constructor() {
        const me: ResetPasswordController = this;
        const app: IApplication = Global.app();

        this.viewModel = {
            // user data
            FirstName: ko.observable(""),
            Email: ko.observable(""),
            Password: ko.observable(""),
            PasswordConfirm: ko.observable(""),
            MinimumPasswordLength: ko.observable(0),
            PasswordComplexityRequirements: ko.observable(""),
            LoginHelp: ko.observable(""),

            // flow control flags
            CheckingKey: ko.observable(false),
            DataLoaded: ko.observable(false),
            ResetCodeInvalid: ko.observable(false),
            ResetComplete: ko.observable(false),
            IsExpired: ko.observable(false),
            IsReset: ko.observable(false),

            // error flags
            PasswordMissing: ko.observable(false),
            PasswordsDoNotMatch: ko.observable(false),
            PasswordInvalid: ko.observable(false),
            PasswordInvalidLength: ko.observable(false),
            PasswordInvalidReason: ko.observable(""),

            // events
            onResetPassword(): void {
                me.resetPassword();
            },
            onGotoDefaultPage(): void {
                app.pageManager().closeAllAndGotoDefaultPage();
            },
            onGotoForgotPasswordPage(): void {
                app.pageManager().closeAndNavigate("/ForgotPassword");
            },
        };

        // computed values
        this.viewModel.isPasswordValid = ko.pureComputed((): boolean => {
            const password: any = me.viewModel.Password();
            let isValid: boolean = false;

            if (password.length >= me.viewModel.MinimumPasswordLength()) {
                isValid = true;
            }

            return isValid;
        });

        this.viewModel.isPasswordConfirmValid = ko.pureComputed((): boolean => {
            const passwordConfirm: any = me.viewModel.PasswordConfirm();
            let isValid: boolean = false;

            if (passwordConfirm.length >= me.viewModel.MinimumPasswordLength() &&
                passwordConfirm === me.viewModel.Password()) {
                isValid = true;
            }

            return isValid;
        });

        this.viewModel.hasLoginHelp = ko.pureComputed((): boolean => {
            return !Util.isNullOrEmpty(me.viewModel.LoginHelp());
        });

    }

    public render(container: JQuery): JQueryPromise<any> {
        const me: ResetPasswordController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;
        ko.applyBindings(this.viewModel, container[0]);

        // process if user presses enter while in a password field
        this.container.find("input[data-id='passwordField']").keypress((e: any) => {
            if (e.which === 13) {
                $(e.target).blur();
                me.resetPassword();
            }
        });

        this.setFocusToFirstField();

        deferred.resolve();
        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        const me: ResetPasswordController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        let validateKeyRequest: AuthenticationData.IValidateResetPasswordKeyRequest;

        if (params.length === 1) {
            this.viewModel.IsReset(true);
            this.token = params[0];
        } else if (params.length === 2) {
            switch (params[0]) {
                case "Expired":
                    this.viewModel.IsExpired(true);
                    break;
                case "Reset":
                    this.viewModel.IsReset(true);
                    break;
                default:
                    // invalid parameter
                    deferred.reject();
                    return deferred.promise();
            }
            this.token = params[1];
        } else {
            // incorrect URL format
            deferred.reject();
            return deferred.promise();
        }

        validateKeyRequest = {
            Token: this.token,
        };

        me.viewModel.CheckingKey(true);
        me.viewModel.DataLoaded(false);
        me.viewModel.ResetCodeInvalid(false);
        // me.viewModel.LoginHelp(app.global.LoginHelp);

        if (Util.isNullOrEmpty(this.token)) {
            // no key passed
            me.viewModel.ResetCodeInvalid(true);
            me.viewModel.CheckingKey(false);
            deferred.resolve();
        } else {
            AuthenticationData.ValidateResetPasswordKey(validateKeyRequest)
                .done((response: Data.IResponse<AuthenticationData.IValidateResetPasswordKeyResponse>) => {
                    if (response.Success && response.Payload.IsValid) {
                        me.updateViewModel(response.Payload);
                    } else {
                        // error loading key info, or key is invalid
                        me.viewModel.ResetCodeInvalid(true);
                    }
                    deferred.resolve();
                })
                .fail(() => {
                    me.viewModel.ResetCodeInvalid(true);
                    deferred.resolve();
                })
                .always(() => {
                    me.viewModel.CheckingKey(false);
                });
        }

        return deferred.promise();

    }

    public haveFieldsChanged(): boolean {
        // no editable fields
        return false;
    }

    public appRouterID(params: any[]): string {
        return "ResetPassword";
    }

    public title(): string {
        return "Change Password";
    }

    public subTitle(): string {
        return "";
    }

    public AllowAnonymousAccess(): boolean {
        return true;
    }

    private updateViewModel(response: AuthenticationData.IValidateResetPasswordKeyResponse): void {
        this.viewModel.FirstName(response.FirstName);
        this.viewModel.Email(response.Email);
        this.viewModel.MinimumPasswordLength(response.MinimumPasswordLength);
        this.viewModel.PasswordComplexityRequirements(response.PasswordComplexityRequirements);

        this.viewModel.Password("");
        this.viewModel.PasswordConfirm("");

        this.viewModel.PasswordMissing(false);
        this.viewModel.PasswordsDoNotMatch(false);
        this.viewModel.PasswordInvalidLength(false);

        this.viewModel.DataLoaded(true);
        this.viewModel.ResetComplete(false);
        this.viewModel.ResetCodeInvalid(false);
    }

    // does client side validation of the password fields
    private validatePassword(): boolean {
        let isValid: boolean = true;
        const password: any = this.viewModel.Password();

        isValid = UI.setRequiredStringError(password, "PasswordMissing", this.viewModel) ? isValid : false;

        if (isValid) {
            if (this.viewModel.Password() !== this.viewModel.PasswordConfirm()) {
                isValid = false;
                this.viewModel.PasswordsDoNotMatch(true);
            } else {
                this.viewModel.PasswordsDoNotMatch(false);

                if (password.length < this.viewModel.MinimumPasswordLength()) {
                    isValid = false;
                    this.viewModel.PasswordInvalidLength(true);
                } else {
                    this.viewModel.PasswordInvalidLength(false);
                }
            }
        }

        return isValid;
    }

    private resetPassword(): void {
        const me: ResetPasswordController = this;
        let request: AuthenticationData.IResetPasswordRequest;
        let waitContainer: JQuery;

        if (this.validatePassword()) {
            waitContainer = UI.showWaitIndicator(this.container);

            request = {
                Password: this.viewModel.Password(),
                Token: this.token,
            };
            AuthenticationData.ResetPassword(request)
                .done((response: Data.IResponse<AuthenticationData.IResetPasswordResponse>) => {
                    const app: IApplication = Global.app();

                    if (response.Success) {
                        if (response.Payload.Success) {
                            // save the user information for use by all other pages
                            app.loginUser(response.Payload.AuthenticationToken, response.Payload.UserSession, response.Payload.ReportToken);

                            // show confirmation message to use
                            me.viewModel.ResetComplete(true);
                        } else {
                            me.viewModel.PasswordInvalid(response.Payload.PasswordInvalid);
                            me.viewModel.PasswordInvalidReason(response.Payload.PasswordInvalidReason);
                            me.setFocusToFirstField();
                        }
                    } else {
                        UI.showErrorMessage(response.ErrorMessages);
                    }
                })
                .always(() => {
                    UI.hideWaitIndicator(me.container, waitContainer);
                });
        } else {
            this.setFocusToFirstField();
        }
    }

    private setFocusToFirstField(): void {
        this.container.find("input[data-id='passwordField']").first().focus();
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/resetPassword.ts